import { isInteger, has } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { resetOnLogout } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
    AssignedColors,
    WebColorsState,
    SetWebColorsPayload
} from './types';

const defaultWebColorsState: WebColorsState = {
    lastIndex: -1,
    assignedColors: {}
};

export const webColorsSlice = createSlice({
    name: 'webColors',
    initialState: defaultWebColorsState,
    reducers: {
        setWebColorsLastIndex: (
            state: WebColorsState,
            action: PayloadAction<number>
        ) => {
            const isPayloadInteger = isInteger(action.payload);
            if (isPayloadInteger) {
                state.lastIndex = action.payload;
            }
            return state;
        },
        setWebColors: (
            state: WebColorsState,
            action: PayloadAction<SetWebColorsPayload>
        ) => {
            if (!action.payload) return state;

            if (
                has(action.payload, 'id') &&
                has(action.payload, 'colors.color') &&
                has(action.payload, 'colors.backgroundColor')
            ) {
                const { id, type, colors } = action.payload;
                const assignedColors = type ? { ...colors, type } : colors;
                state.assignedColors[id] = assignedColors;
            }
            return state;
        },
        setWebColorsInBulk: (
            state: WebColorsState,
            action: PayloadAction<AssignedColors>
        ) => {
            if (!action.payload) return state;
            state.assignedColors = {
                ...state.assignedColors,
                ...action.payload
            };
            return state;
        },
        resetRouteWebColors: (state: WebColorsState) => {
            const newState = { ...state };
            const asArray = Object.entries(newState.assignedColors);
            const filtered = asArray.filter((item) => {
                return item[1].type === 'driver';
            });
            const lastIndex = filtered.length - 1;
            const assignedColors = Object.fromEntries(filtered);

            return { ...state, lastIndex, assignedColors };
        },
        resetWebColors: () => {
            return defaultWebColorsState;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetOnLogout, () => {
            return defaultWebColorsState;
        });
    }
});

export const {
    setWebColorsLastIndex,
    setWebColors,
    resetRouteWebColors,
    resetWebColors,
    setWebColorsInBulk
} = webColorsSlice.actions;

export const selectWebColors = (state: RootState): AssignedColors =>
    state.webColors.assignedColors;

export default webColorsSlice.reducer;
